import axiosInstance from '../axiosInstance';
import { getManagerRoutePath } from '../managerRouteUtil';

/**
 * Care Planner — /api/carehome-manager/care-planner (care homes only).
 *
 * A "plan" is everything scheduled together in one Plan care dialog; a
 * "task" is one occurrence of it on one day. Plans are created and edited as
 * a whole; tasks are reassigned, marked missed and reviewed one at a time.
 *
 * The API derives everything clock-dependent — overdue, due now, late, needs
 * review, needs allocation — and sends it on each task, so the planner draws
 * what comes back rather than recomputing it.
 *
 * Calls throw the raw axios error; pages read the message with
 * errorMessage() from ./care_planner/shared.
 */

const base = () => `/${getManagerRoutePath()}/care-planner`;
const unwrap = (response) => response.data;

const carePlannerService = {
  // Library, residents, carers, resident groups, outcomes, priorities.
  getMeta: async () => unwrap(await axiosInstance.get(`${base()}/meta`)),

  // Every occurrence from..to (YYYY-MM-DD, inclusive) plus the rota for it.
  getTasks: async (from, to) => unwrap(await axiosInstance.get(`${base()}/tasks`, { params: { from, to } })),

  // { residence_id, actions: [code], from, to, weekdays: [0-6], start_time, end_time,
  //   staff_required, assigned_to, second_assignee, priority, instructions, notify }
  createPlan: async (payload) => unwrap(await axiosInstance.post(`${base()}/plans`, payload)),
  getPlan: async (planId) => unwrap(await axiosInstance.get(`${base()}/plans/${planId}`)),
  updatePlan: async (planId, payload) => unwrap(await axiosInstance.put(`${base()}/plans/${planId}`, payload)),
  deletePlan: async (planId) => unwrap(await axiosInstance.delete(`${base()}/plans/${planId}`)),

  // One occurrence: carers, and (until started) time, staffing, priority, instructions.
  updateTask: async (id, payload) => unwrap(await axiosInstance.patch(`${base()}/tasks/${id}`, payload)),
  deleteTask: async (id) => unwrap(await axiosInstance.delete(`${base()}/tasks/${id}`)),
  markMissed: async (id, reason) => unwrap(await axiosInstance.post(`${base()}/tasks/${id}/missed`, { reason })),
  reviewTask: async (id, note) => unwrap(await axiosInstance.post(`${base()}/tasks/${id}/review`, { note })),

  createGroup: async (payload) => unwrap(await axiosInstance.post(`${base()}/groups`, payload)),
  updateGroup: async (id, payload) => unwrap(await axiosInstance.put(`${base()}/groups/${id}`, payload)),
  deleteGroup: async (id) => unwrap(await axiosInstance.delete(`${base()}/groups/${id}`)),

  // What can be planned for one resident: the care-note categories and
  // actions the app shows them, their own actions included (`own: true`).
  getCareActions: async (residenceId) => unwrap(await axiosInstance.get(`${base()}/residents/${residenceId}/care-actions`)),
};

export default carePlannerService;
